'use client'

import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect, useRef } from 'react'
import { useUser } from '@clerk/nextjs'
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store'
import { useLogger } from '@/lib/hooks/useLogger'

export function PostHogAnalyticsProvider({ children }: { children?: React.ReactNode }) {
  const { user: clerkUser } = useUser()
  const mixusUser = useUserStore(state => state.user)
  const logger = useLogger()
  const isInitialized = useRef(false)

  useEffect(() => {
    try {
      if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) {
        logger.warn('PostHog key not found')
        return
      }

      if (isInitialized.current) return
      
      logger.info('Initializing PostHog')

      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com',
        capture_pageview: true,
        autocapture: true,
        persistence: 'localStorage',
        session_recording: {
          maskAllInputs: true,
          maskInputOptions: {
            password: true,
            email: true,
            number: true,
            search: true,
          }
        },
        loaded: (posthog) => {
          if (process.env.NODE_ENV === 'development') {
            posthog.debug()
          }
          logger.info('PostHog loaded successfully', {
            distinctId: posthog.get_distinct_id(),
            sessionId: posthog.onSessionId,
            hasIdentity: !!posthog.identify
          })
        },
        bootstrap: {
          distinctID: mixusUser?._id || clerkUser?.id || undefined,
        }
      })
    } catch (error) {
      logger.error('Failed to initialize PostHog:', error)
    }
  }, [logger, mixusUser?._id, clerkUser?.id])

  useEffect(() => {
    if (!isInitialized.current) return

    try {
      if (mixusUser?._id || clerkUser?.id) {
        logger.info('Identifying user in PostHog')
        posthog.identify(mixusUser?._id || clerkUser?.id, {
          email: clerkUser?.emailAddresses?.[0]?.emailAddress,
          name: mixusUser?.fullName || clerkUser?.fullName,
          username: mixusUser?.username,
          clerkUserId: clerkUser?.id,
          mixusUserId: mixusUser?._id,
          lastIdentifiedAt: new Date().toISOString()
        })
      }
    } catch (error) {
      logger.error('Failed to identify user in PostHog:', error)
    }
  }, [clerkUser, mixusUser, logger])

  useEffect(() => {
    return () => {
      if (isInitialized.current) {
        try {
          posthog.reset()
          isInitialized.current = false
          logger.info('PostHog reset on unmount')
        } catch (error) {
          logger.error('Failed to reset PostHog:', error)
        }
      }
    }
  }, [logger])

  return children ? <PostHogProvider client={posthog}>{children}</PostHogProvider> : null
} 